import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpDispatchListService from "@plannel/services/fp/fp-dispatch-list-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, { buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildFlatReportStatusBar, readHide } from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — planresult/PlanResult 파일럿에서 확인된 값(9)을
// 그대로 재사용한다(fp-report-screen-guide.md §A-3c, 이 화면도 같은 결정을 따름).
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 3개(loadData의 getValues 기준) —
// DispatchListQueryRepository#makeWhereClause가 resourceCdNm/routeCdNm/woCdNm 전부 contains 조건으로 소비.
const SEARCH_FIELD_LABELS = { resourceCdNm: "FP_RESOURCE", routeCdNm: "FP_ROUTE", woCdNm: "FP_ORDER_CD" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 행 그룹(트리) 기준 — old 컬럼정의의 resourceGroup(horizontal, resourceCd/resourceNm)을
// Row Grouping으로 재현(fp-report-screen-guide.md §A-6b/§B — 이 화면은 크로스탭이 아니라 평평한
// 목록이라 나머지 컬럼(startTs~relResourceTpCd)에 걸린 old mergeRule은 "그레인까지 기계적으로 건
// 세로 병합"으로 판단해 행 그룹으로 만들지 않고 평범한 leaf 필드로 내린다 — §A-6b 판단 기준).
const GROUP_FIELDS = ["resourceCd", "resourceNm"];
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_NM_HIDE_KEY = "dispatchListResourceNmColsHide";

const DispatchList = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  // 자유 텍스트 조회조건 — Report 공통 정책(§A-9b)대로 조회 버튼이 없어 ref(uncontrolled)로 소유하고
  // 필드 안의 검색 아이콘/Enter로만 수동 커밋한다(SearchOptsGroupRetrieve).
  const resourceCdNmRef = useRef("");
  const routeCdNmRef = useRef("");
  const woCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const latestReqRef = useRef(0);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveDispatchList(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old gridItems 1:1 대조 — resourceGroup(2) + startTs~relResourceTpCd(15) = 17개 필드.
  // customerCd/routeCd/relRouteCd/relResourceCd는 old도 visible:false라 hide:true로 유지(엑셀
  // export/컬럼설정에서 여전히 선택 가능해야 하므로 컬럼 자체는 남긴다).
  const columnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY),
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 200
    }),
    { headerName: "FP_START_TS", field: "startTs", colId: "startTs", width: 180, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_END_TS", field: "endTs", colId: "endTs", width: 180, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_ORDER_CD", field: "woCd", colId: "woCd", width: 170 },
    { headerName: "FP_PRIORITY", field: "woPriority", colId: "woPriority", width: 100, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_CUSTOMER_CD", field: "customerCd", colId: "customerCd", width: 130, hide: true },
    { headerName: "FP_CUSTOMER_NM", field: "customerNm", colId: "customerNm", width: 160 },
    { headerName: "FP_PRODUCTION_QTY", field: "qty", colId: "qty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", width: 130, hide: true },
    { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", width: 170 },
    // old headerText transLangKey("CATEGORY") — Previous/Post 구분 리터럴("Previous"/"Post",
    // DispatchListQueryRepository가 상수로 내려줌). 값 자체는 old도 번역하지 않고 그대로 노출.
    { headerName: "CATEGORY", field: "category", colId: "category", width: 110 },
    // old displayCallback(timeToString)은 구 백엔드가 초 단위 숫자를 내려줄 때의 변환이었고,
    // 새 백엔드(DispatchListService)는 이미 "시/분/초" 포맷 문자열로 내려주므로 그대로 표시한다.
    { headerName: "FP_WAIT_TIME", field: "duration", colId: "duration", width: 130 },
    { headerName: "FP_RELATED_ROUTE_CD", field: "relRouteCd", colId: "relRouteCd", width: 130, hide: true },
    { headerName: "FP_RELATED_ROUTE_NM", field: "relRouteNm", colId: "relRouteNm", width: 170 },
    { headerName: "FP_RELATED_RESOURCE_CD", field: "relResourceCd", colId: "relResourceCd", width: 140, hide: true },
    { headerName: "FP_RELATED_RESOURCE_NM", field: "relResourceNm", colId: "relResourceNm", width: 150 },
    { headerName: "FP_RELATED_RESOURCE_TP_CD", field: "relResourceTpCd", colId: "relResourceTpCd", width: 130, type: ["centerAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const retrieveDispatchList = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      resourceCdNm: resourceCdNmRef.current,
      routeCdNm: routeCdNmRef.current,
      woCdNm: woCdNmRef.current,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
      // TODO(MIG-T2): [dispatchlist/DispatchList] localeDateFormat(사용자별 날짜 포맷) 미전송.
      //   old: localeStoreApi.getState().dateFormat을 실어 보내 DispatchListService가 startTs/endTs의
      //        날짜 부분을 그 포맷으로 재정규화
      //   사유: 안 보내면 백엔드 기본 포맷(Bucket.DEFAULT_DATE_FORMAT)으로 처리되고, 그리드 표시는
      //        어차피 dateUtils.formatDateTime으로 다시 포맷하므로 화면상 차이가 없음(1차 목표에 불필요)
    };

    gridRef.current?.api?.setGridOption("loading", true);

    fpDispatchListService.getDispatchList(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = response.data?.data ?? [];
        setRowData(rows);

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const statusBar = useMemo(() => buildFlatReportStatusBar(), []);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod — PlanResult.js/ResourceUtilization.js와 동일 원리).
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRowData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        {/* old useBucket={false} — 이 화면은 버킷 개념이 없는 활동(activity) 목록이라 버킷 Select를 숨긴다. */}
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          useBucket={false}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ resourceCdNm: resourceCdNmRef, routeCdNm: routeCdNmRef, woCdNm: woCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveDispatchList}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <Box id="fp-dispatch-list-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, mt: 1, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          {...defaultGridMemo}
          statusBar={statusBar}
          onGridReady={onGridReady}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(DispatchList);
